import {
    Box,
    BoxProps,
    Chip,
    Grid2 as Grid,
    Grid2Props,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import TabPanel from '@atoms/TabPanel';
import useLocationQuery from '@hooks/useLocationQuery';
import ErrorBoundary from '@atoms/ErrorBoundary';

// encode tab names to URL friendly string
const encodeTabName = (tabName: string) =>
    encodeURI(tabName.toLocaleLowerCase().replace(/\s/g, '-'));

export const QUERY_KEY = 'tab';

interface Props extends Partial<Grid2Props> {
    doNotNavigateURL?: boolean;
    tabBoxProps?: Partial<BoxProps>;
    tabs: {
        length?: number;
        tabName: string;
        renderFn: () => JSX.Element | null;
    }[];
    vertical?: boolean;
    confirmTabChange?: () => boolean;
}

const getQueryString = (tabName: string) =>
    new URLSearchParams({
        [QUERY_KEY]: tabName,
    });

function NestedTabNavigation({
    confirmTabChange = () => true,
    doNotNavigateURL,
    tabs,
    tabBoxProps = {},
    vertical,
    ...rest
}: Props) {
    const [, setSearchParams] = useSearchParams();
    const query = useLocationQuery();
    const theme = useTheme();
    const mdBreakpoint = useMediaQuery(theme.breakpoints.up('md'));

    // Set the selected tab to the tab in the URL or the first tab before initial render
    const [selectedTab, setSelectedTab] = useState<string | null>(() => {
        const defaultToFirstTab = () => (tabs.length > 0 ? encodeTabName(tabs[0].tabName) : null);

        if (doNotNavigateURL) {
            // If not navigating URL, default to first tab
            return defaultToFirstTab();
        } else {
            const oldTabName = query.get(QUERY_KEY);
            // if the old tab name exists in the tabs, set it as the selected tab
            if (oldTabName && tabs.find((t) => encodeTabName(t.tabName) === oldTabName)) {
                return oldTabName;
            } else {
                return defaultToFirstTab();
            }
        }
    });

    // This effect keeps the url in sync with the selected tab
    useEffect(() => {
        const urlQuery = query.get(QUERY_KEY);
        if (selectedTab !== urlQuery) {
            const selectedTabExists = tabs.find((t) => encodeTabName(t.tabName) === selectedTab);
            if (selectedTabExists) {
                // only update url if navigation is enabled
                if (!doNotNavigateURL)
                    // Set url to selected tab
                    setSearchParams(getQueryString(encodeTabName(selectedTabExists.tabName)), {
                        replace: true,
                    });
            } else {
                // Set url to first tab
                setSelectedTab(encodeTabName(tabs[0].tabName));
            }
        }
    }, [doNotNavigateURL, query, selectedTab, setSearchParams, tabs]);

    const handleChangeTab = (_event: unknown, newValue: string) => {
        if (confirmTabChange()) setSelectedTab(newValue);
    };

    return (
        <Grid
            container
            direction={vertical ? 'row' : 'column'}
            display="block" // fix horizontal overflow?
            {...rest}
            sx={{ marginTop: '-16px', marginLeft: '-16px', width: '100%', ...(rest.sx || {}) }}
        >
            <Grid
                display={tabs.length && tabs.length > 1 ? 'block' : 'none'}
                size={{ xs: 12, md: vertical ? 2 : 12 }}
            >
                {selectedTab ? (
                    <Tabs
                        allowScrollButtonsMobile
                        onChange={handleChangeTab}
                        orientation={vertical && mdBreakpoint ? 'vertical' : 'horizontal'}
                        sx={
                            vertical && mdBreakpoint
                                ? { position: 'fixed' }
                                : { position: 'relative' }
                        }
                        value={selectedTab}
                        variant="scrollable"
                    >
                        {tabs.map((tab) => {
                            return (
                                <Tab
                                    key={tab.tabName}
                                    label={
                                        vertical ? (
                                            <Box flexDirection="row" display="flex">
                                                <Typography
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        alignItems: 'center',
                                                    }}
                                                    variant="inherit"
                                                >
                                                    {tab.tabName}
                                                </Typography>
                                                <Chip
                                                    sx={{
                                                        marginLeft: '.5rem',
                                                        backgroundColor: 'rgb(1,1,1,0.1)',
                                                        color: 'rgb(0,0,0,.4)',
                                                    }}
                                                    label={tab.length}
                                                    color="secondary"
                                                    size="small"
                                                />
                                            </Box>
                                        ) : (
                                            <Typography variant="inherit">{tab.tabName}</Typography>
                                        )
                                    }
                                    value={encodeTabName(tab.tabName)}
                                    wrapped
                                />
                            );
                        })}
                    </Tabs>
                ) : null}
            </Grid>
            <Grid
                size={{ xs: 12, md: vertical ? 2 : 12, lg: 12 }}
                sx={vertical && mdBreakpoint ? { marginLeft: '165px' } : {}}
            >
                {tabs.map((tab) => (
                    <ErrorBoundary key={tab.tabName} location="Nested Tab">
                        <TabPanel
                            value={selectedTab}
                            index={encodeTabName(tab.tabName)}
                            boxProps={tabBoxProps}
                        >
                            {tab.renderFn()}
                        </TabPanel>
                    </ErrorBoundary>
                ))}
            </Grid>
        </Grid>
    );
}

export default memo(NestedTabNavigation);
